<template>
  <div
    :class="{ 'inline-flex h-full': $slots.default }"
    @contextmenu.prevent="handleContextMenu"
  >
    <!-- Mobile fixed bottom sheet mode -->
    <template v-if="isFixed">
      <div
        :id="uuid + '-contents'"
        class="contents"
        @click="handleClick"
      >
        <slot ref="trigger" v-bind="props">
          <template v-if="!anchor">
            <div class="flex items-center">
              <o-button
                v-if="split"
                :flat="flat"
                :outline="outline"
                :variant="variant"
                :icon="icon"
                :icon-size="12"
                rounded="rounded-l-lg mr-px"
                @click.stop.prevent="$emit('click')"
              >
                {{ label }}
              </o-button>
              <o-button
                ref="button"
                :flat="flat"
                :outline="outline"
                :variant="variant"
                :rounded="split ? 'rounded-r-lg' : 'rounded-lg'"
                icon="chevronDown"
                icon-after
                :icon-size="12"
              >
                <template v-if="!split">
                  {{ label }}
                </template>
              </o-button>
            </div>
          </template>
        </slot>
      </div>

      <Teleport to="#dialog-container">
        <transition name="fade" appear>
          <div
            v-if="show"
            :class="[
              'fixed inset-0 z-80000',
              { 'bg-black/10': overlay || isFixed }
            ]"
            @click="close()"
          />
        </transition>
        <dropdown-provider v-bind="dropdownProviderProps">
          <transition name="slide-up">
            <div v-if="show" :class="fixedDropdownClass">
              <transition name="fade">
                <div
                  v-if="loading"
                  class="absolute h-full w-full inset-0 bg-white/25 backdrop-blur z-80000 dark:bg-gray-900"
                >
                  <div class="h-full w-full flex items-center justify-center">
                    <o-loader />
                  </div>
                </div>
              </transition>
              <slot name="content" v-bind="props">
                <div class="flex flex-col divide-y opacity-80">
                  <dropdown-item
                    v-for="(item, itemIndex) in items"
                    :key="`item-${itemIndex}`"
                    :name="item.name"
                    :description="item.description"
                    :icon="item.icon"
                    v-on="item.events"
                  />
                </div>
              </slot>
            </div>
          </transition>
        </dropdown-provider>
      </Teleport>
    </template>

    <!-- Desktop: UPopover-powered positioning -->
    <template v-else>
      <UPopover
        :open="show"
        :mode="popoverMode"
        :content="popoverContent"
        :arrow="arrow ? { rounded: true } : false"
        :portal="portal"
        :dismissible="clickaway"
        :open-delay="popoverMode === 'hover' ? 100 : 0"
        :close-delay="popoverMode === 'hover' ? 100 : 0"
        :ui="{
          content: [
            containerClass,
            sameWidth ? 'w-(--reka-popper-anchor-width)' : '',
            translucent ? 'backdrop-blur backdrop-filter' : '',
          ].filter(Boolean).join(' '),
        }"
        @update:open="handlePopoverUpdate"
      >
        <slot ref="trigger" v-bind="props">
          <template v-if="!anchor">
            <div class="flex items-center">
              <o-button
                v-if="split"
                :flat="flat"
                :outline="outline"
                :variant="variant"
                :icon="icon"
                :icon-size="12"
                rounded="rounded-l-lg mr-px"
                @click.stop.prevent="$emit('click')"
              >
                {{ label }}
              </o-button>
              <o-button
                ref="button"
                :flat="flat"
                :outline="outline"
                :variant="variant"
                :rounded="split ? 'rounded-r-lg' : 'rounded-lg'"
                icon="chevronDown"
                icon-after
                :icon-size="12"
              >
                <template v-if="!split">
                  {{ label }}
                </template>
              </o-button>
            </div>
          </template>
        </slot>

        <template #content="{ close: popoverClose }">
          <dropdown-provider v-bind="dropdownProviderProps">
            <div :class="[padding, defaultClass]">
              <transition name="fade">
                <div
                  v-if="loading"
                  class="absolute h-full w-full inset-0 bg-white/25 backdrop-blur z-80000 dark:bg-gray-900 rounded"
                >
                  <div class="h-full w-full flex items-center justify-center">
                    <o-loader />
                  </div>
                </div>
              </transition>
              <slot name="content" v-bind="props">
                <div class="flex flex-col divide-y opacity-80">
                  <dropdown-item
                    v-for="(item, itemIndex) in items"
                    :key="`item-${itemIndex}`"
                    :name="item.name"
                    :description="item.description"
                    :icon="item.icon"
                    v-on="item.events"
                  />
                </div>
              </slot>
            </div>
          </dropdown-provider>
        </template>
      </UPopover>
    </template>
  </div>
</template>

<script lang="ts">
export default { name: 'ODropdown' }
</script>

<script setup lang="ts">
import { useUIStore } from '~/stores/useUIStore'
import DropdownProvider from './DropdownProvider.vue'
import DropdownItem from './DropdownItem.vue'

const uiStore = useUIStore()
const inst = getCurrentInstance()

interface Props {
  anchor?: any
  trigger?: string[]
  placement?: string
  boundary?: string
  portal?: boolean
  autoHide?: boolean
  autoHideParent?: boolean
  overlay?: boolean
  clickaway?: boolean
  arrow?: boolean
  items?: any[]
  disabled?: boolean
  loading?: boolean
  label?: string
  containerClass?: string
  defaultClass?: string
  padding?: string
  sameWidth?: boolean
  detectOverflow?: boolean
  translucent?: boolean
  flat?: boolean
  outline?: boolean
  split?: boolean
  fixed?: boolean
  icon?: string | null
  variant?: string | null
  modifiers?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  anchor: null,
  trigger: () => ['click'],
  placement: 'bottom-end',
  boundary: 'scrollParent',
  portal: true,
  autoHide: true,
  autoHideParent: false,
  overlay: false,
  clickaway: true,
  arrow: true,
  items: () => [],
  disabled: false,
  loading: false,
  label: 'dropdown',
  containerClass: '',
  defaultClass: 'min-w-52',
  padding: 'py-1 px-0',
  sameWidth: false,
  detectOverflow: false,
  translucent: false,
  flat: false,
  outline: false,
  split: false,
  fixed: true,
  icon: null,
  variant: null,
  modifiers: () => [
    { name: 'offset', options: { offset: [0, 8] } },
  ],
})

const emit = defineEmits<{
  (e: 'click'): void
  (e: 'show'): void
  (e: 'shown'): void
  (e: 'hide'): void
  (e: 'hidden'): void
  (e: 'close'): void
}>()

const uuid = String((inst as any)?.uid || Math.random().toString(36).slice(2))

const show = ref(false)
const parentDropdown = inject<any>('dropdown', null)

// Provide dropdown context for DropdownField (used as trigger, outside DropdownProvider)
provide('dropdown', () => ({
  close,
  closeTree,
  open,
  show: show.value,
  autoHide: props.autoHide,
}))

const isMobile = computed(() => uiStore.isMobile)
const isFixed = computed(() => isMobile.value && props.fixed)

const useTriggerClick = computed(() => props.trigger.includes('click'))
const useTriggerHover = computed(() => props.trigger.includes('hover'))
const useTriggerContextMenu = computed(() => props.trigger.includes('contextmenu'))

const popoverMode = computed(() => {
  if (useTriggerHover.value) return 'hover'
  return 'click'
})

/**
 * Map Popper.js placement string to Reka UI content positioning.
 * e.g. 'bottom-end' → { side: 'bottom', align: 'end' }
 */
const popoverContent = computed(() => {
  const [side, align] = props.placement.split('-')

  // Extract offset from modifiers if present
  const offsetMod = props.modifiers?.find((m: any) => m.name === 'offset')
  const sideOffset = offsetMod?.options?.offset?.[1] ?? 8

  return {
    side: side as 'top' | 'right' | 'bottom' | 'left',
    align: (align || 'center') as 'start' | 'center' | 'end',
    sideOffset,
    collisionPadding: 8,
    reference: getAnchorElement() || undefined,
    onInteractOutside: handleInteractOutside,
  }
})

const fixedDropdownClass = computed(() => [
  props.padding,
  'w-full h-auto fixed bottom-0 left-0 right-0 z-80000 bg-white dark:bg-gray-900 rounded-t-lg shadow-lg',
  props.defaultClass,
])

const dropdownProviderProps = computed(() => ({
  close,
  closeTree,
  open,
  show: show.value,
  autoHide: props.autoHide,
}))

function handleClick () {
  if (isFixed.value && useTriggerClick.value && !props.disabled) {
    show.value = !show.value
  }
}

function handleContextMenu () {
  if (useTriggerContextMenu.value && !props.disabled) {
    show.value = !show.value
  }
}

function handlePopoverUpdate (value: boolean) {
  if (props.disabled) return
  show.value = value
}

function getAnchorElement () {
  const value = props.anchor
  if (!value || typeof HTMLElement === 'undefined') return null

  const element = value.$el || value
  return element instanceof HTMLElement ? element : null
}

function handleInteractOutside (event: any) {
  const anchorElement = getAnchorElement()
  const target = event.target || event.detail?.originalEvent?.target

  if (
    anchorElement &&
    target &&
    typeof Node !== 'undefined' &&
    target instanceof Node &&
    anchorElement.contains(target)
  ) {
    event.preventDefault()
  }
}

function open () {
  show.value = true
}

function close () {
  show.value = false
}

function closeTree () {
  close()

  if (props.autoHideParent) {
    const closeParent = parentDropdown?.closeTree || parentDropdown?.close
    closeParent?.()
  }
}

watch(show, (val) => {
  if (val) {
    emit('show')
    emit('shown')
  } else {
    emit('close')
    emit('hide')
    emit('hidden')
  }
})

defineExpose({ open, close, closeTree, uuid, show })
</script>

<style lang="scss">
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s, opacity 0.3s;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(50px);
}
</style>
